import React, {
  useState,
  useEffect,
  useContext,
  FC,
  ChangeEvent,
  useReducer,
  useRef,
} from 'react'
import SwapInput from './SwapInput'
import SwapPanel from './SwapPanel'
import SwapTitle from './SwapTitle'
import SwapInputPanel from './SwapInputPanel'
import SwapInputCurrency from './SwapInputCurrency'
import SwapButton from './SwapButton'
import SwapCurrencySelectorPanel from './SwapCurrencySelectorPanel'
import SwapQuotation from './SwapQuotation'
import {
  SuccessSwap,
  Swap as SwapObject,
  ValidSwap,
} from '../../logic/swaps/Swap'

import {
  defaultSlippage,
  getSupportedChain,
  numericalPlaceholder,
} from '../../config'

import { Web3Context } from '../../hooks/web3'

import { EmptyLine } from '../catalog/EmptyLine'
import { ArrowDown } from '../catalog/ArrowDown'
import { ArrowDownAbsoluteContainer } from '../catalog/ArrowDownAbsoluteContainer'
import { MaxButton } from '../catalog/MaxButton'
import styled from 'styled-components'
import {
  fromWei,
  numericTextAsWei,
  toWei,
  weiToNumericText,
} from '../../utils/static-web3'
import { SwapApi } from '../../api/web3/swap-api'
import { Address, ChainConfiguration, Wei } from '../../utils/types'
import { PreSwapPopup } from './preswap/PreSwapPopup'
import { PostSwapPopup } from './preswap/PostSwapPopup'
import { FailPostSwapPopup } from './preswap/FailPostViewPopup'
import { Gear } from '../catalog/icons/Gear'
import { Settings } from './Settings'
import { priceFormatter } from '../../logic/financials/precise'
import SimpleBalance from '../catalog/SimpleBalance'
import { TokenApi } from '../../api/web3/token-api'
import { Token } from '../../logic/token/token'
import { swaapRed0, SwapStyle } from '../Themes'
import { oracleApi } from '../../api/oracle/oracle-client'
import { ViewState } from '../pools/deposit/DepositState'
import { extractErrorMessage } from '../../logic/errors/extract-error-message'
import { ProxyApi } from '../../api/web3/proxy-api'
import { useTransactions } from '../transactions/TransactionContext'

import {
  TOKEN_SELECTOR_INITIAL_STATE,
  TokenSelector,
  tokenSelectorReducer,
} from './reducers/token-selector-reducer'
import {
  SWAP_INITIAL_STATE,
  swapReducer,
  SwapReducerState,
} from './reducers/swap-reducer'
import { FUNNEL_INITIAL_STATE, funnelReducer } from './reducers/funnel-reducer'
import { SwapState } from './SwapState'
import {
  BALANCE_INITIAL_STATE,
  balanceReducer,
} from './reducers/balance-reducer'
import { useReloader } from '../../hooks/use-reload'
import { useDebounce } from '../../hooks/use-debounce'
import { isCypressInjected } from '../connection/provider'
import { createTradeTabEvent } from 'user-events-segment'
import { Amounts } from '../../logic/token/amounts'
import { wait } from '../../utils/wait'

const { PREVIEW, PROCESSING } = SwapState

/**
 * The responseNonce is used with combination of debounced actionTreatment
 * There may be some concurrent calls to the api. The stack reference will increase at each call, and each call will
 * reference the stackReference
 * In that way, we can make sure that we ignore intermediate calls that will arrive AFTER the last call referenced by the
 * higher value of stackReference
 */
let responseNonce = 0
let lastTreatment = 0

export const Swap: FC = () => {
  const web3Context = useContext(Web3Context)

  const { nonce, connected, account } = useReloader()
  const poolContractAddress = web3Context['poolContractAddress']
  const whitelist = web3Context['whitelist']
  const defaultToken = web3Context['defaultToken']

  const [userActionNonce, setUserActionNonce] = useState(0)
  const actionTreatmentNonce = useDebounce(userActionNonce, 1)

  const [balanceIn, balanceInDispatcher] = useReducer(
    balanceReducer,
    BALANCE_INITIAL_STATE
  )
  const [balanceOut, balanceOutDispatcher] = useReducer(
    balanceReducer,
    BALANCE_INITIAL_STATE
  )

  const tokenInLoading = balanceIn.loading
  const tokenInBalance = balanceIn.value
  const showTokenInBalance = balanceIn.displayed
  const tokenOutLoading = balanceOut.loading
  const tokenOutBalance = balanceOut.value
  const showTokenOutBalance = balanceOut.displayed

  // swap parameters state
  const [slippage, setSlippage] = useState<number>(defaultSlippage)

  // Token-selector-reducer
  const [tokenSelectorState, tokenSelectorDispatcher] = useReducer(
    tokenSelectorReducer,
    TOKEN_SELECTOR_INITIAL_STATE
  )

  // Swap-reducer
  const [currentSwap, swapDispatcher] = useReducer(
    swapReducer,
    SWAP_INITIAL_STATE
  )
  const swapTokenIn = currentSwap.tokenIn
  const swapTokenOut = currentSwap.tokenOut
  const tokenInValue = currentSwap.tokenInValue
  const tokenOutValue = currentSwap.tokenOutValue
  const expectedOutAmountWei = currentSwap.expectedOutWei
  const swapTokenInText = currentSwap.tokenInText
  const maxSelected = currentSwap.max
  const exceed = currentSwap.exceed

  // Settings Popup
  const [showSettings, setShowSettings] = useState(false)

  // State for our modal
  const [funnel, funnelDispatcher] = useReducer(
    funnelReducer,
    FUNNEL_INITIAL_STATE
  )

  const state = funnel.state
  const errorMessage = funnel.errorMessage
  /* Define if user inputs too much money*/

  // TODO: this is the queue ; must not be handled here, but on another part of the app
  const [successfulSwap, setSuccessfulSwap] = useState<SwapObject | undefined>(
    undefined
  )
  // TODO: should not use this state any more
  const [preparingNextTransaction, setPreparingNextTransaction] =
    useState(false)

  // TODO: rename as transaction dispatcher
  const { transactions, dispatcher } = useTransactions()

  const [swap, setSwap] = useState<SwapObject | undefined>(undefined)

  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  })

  // We consider the beginning when we receive the context
  useEffect(() => {
    initComponent()
  }, [])

  function initComponent() {
    // TODO: create a useSlippage
    // slippage from localStorage. It's a 0.003 to represent '0.3%'
    const lsSlippage = window.localStorage.getItem('slippage')
    if (lsSlippage) {
      setSlippage(Number(lsSlippage))
    }

    oracleApi.getPrices().then(() => swapDispatcher({ type: 'oracle-loaded' }))

    if (isCypressInjected()) {
      funnelDispatcher({ type: 'connect' })
    }
  }

  useEffect(() => {
    swapDispatcher({
      type: 'update-tokenInBalance',
      tokenInBalance: balanceIn.value,
    })
  }, [balanceIn])

  useEffect(() => {
    // WETH by default
    swapDispatcher({
      type: 'selected-in',
      selection: defaultToken,
      selectionMode: 'selected_default',
    })
  }, [defaultToken])

  // Account and connection handling
  useEffect(() => {
    initAccount()
  }, [nonce])

  function initAccount() {}

  useEffect(() => {
    updateFunnel()
  }, [
    nonce,
    currentSwap.tokenIn,
    currentSwap.tokenOut,
    currentSwap.tokenInText,
    currentSwap.expectedOutWei,
    tokenInBalance,
  ])

  function updateFunnel() {
    // Checking if exceed first. There are some subtilities comparing undefined numbers

    if (currentSwap.estimating) {
      // to avoid flickering while loading. But we still want to show if exceed.
      return
    }

    // Now checking funnel
    if (!connected) {
      return funnelDispatcher({ type: 'connect' })
    }
    if (!currentSwap.tokenIn || !currentSwap.tokenOut) {
      return funnelDispatcher({ type: 'choose-token' })
    }

    if (
      !isValidAmount(currentSwap.tokenInWei!) ||
      !isValidAmount(currentSwap.expectedOutWei!)
    ) {
      return funnelDispatcher({ type: 'choose-amount' })
    }

    if (Number(currentSwap.tokenInWei) > Number(tokenInBalance)) {
      return funnelDispatcher({ type: 'choose-amount' })
    }
    funnelDispatcher({ type: 'swappable' })
  }

  // For balances
  useEffect(() => {
    loadBalances()
  }, [nonce, swapTokenIn, swapTokenOut, successfulSwap])

  useEffect(() => {
    setTimeout(() => loadBalances(), 100)
  }, [transactions.pending.length])

  // TODO : still a bit complex
  useEffect(() => {
    lastTreatment = actionTreatmentNonce
    if (state === PROCESSING) {
      // can't create a new swap object
      return
    }
    if (swapTokenIn && swapTokenOut && isValidAmount(swapTokenInText)) {
      const newSwap = createSwapObject(
        web3Context.config,
        currentSwap,
        poolContractAddress,
        slippage,
        actionTreatmentNonce
      )

      // we just asked for a treatment

      swapTokenQuickEstimation(newSwap)
        .catch((error: any) => {
          console.error('Error while estimating swap token', error)
          swapDispatcher({ type: 'expectation-cleared' })
          newSwap.setExpectedOutAmount(undefined)
        })
        .then(expectation => {
          responseNonce = lastTreatment
          if (expectation) {
            if (newSwap.actionTreatmentNonce < responseNonce) {
              // It comes from an old swap treatment that took too much time. Ignore
              console.debug('Old swap treatment got a response: ignored', {
                swapActionTreatmentNonce: newSwap.actionTreatmentNonce,
                responseNonce,
                lastTreatment,
              })
            } else if (responseNonce === newSwap.actionTreatmentNonce) {
              swapDispatcher({ type: 'expectation', expectation })
              newSwap.setExpectedOutAmount(expectation)
              setSwap(newSwap) // we have a new swap object candidate
            } // else it's an older value, just ignore it
            else {
              console.error('responseNonce < actionTreatmentNonce', {
                stackReference: responseNonce,
                stackDebounced: actionTreatmentNonce,
              })
            }
          } else {
            // TODO: remove after simplification
            console.error('No expectation after call', { expectation })
            swapDispatcher({ type: 'expectation-cleared' })
            newSwap.setExpectedOutAmount(undefined)
          }
        })
    } else {
      swapDispatcher({ type: 'expectation-cleared' })
    }
  }, [actionTreatmentNonce])

  useEffect(() => {
    setUserActionNonce(userActionNonce + 1)
  }, [nonce, swapTokenIn, swapTokenOut, swapTokenInText])

  function resetSwapParametersState() {
    if (!preparingNextTransaction) {
      swapDispatcher({ type: 'reset' })
    }

    setPreparingNextTransaction(false)
  }

  function validateCurrency(currency: Token) {
    return !(
      (swapTokenIn != undefined && swapTokenIn == currency) ||
      (swapTokenOut != undefined && swapTokenOut == currency)
    )
  }

  function selectCurrency(currency: Token) {
    if (validateCurrency(currency)) {
      if (tokenSelectorState.selector === TokenSelector.IN) {
        swapDispatcher({
          type: 'selected-in',
          selection: currency,
        })
      } else if (tokenSelectorState.selector === TokenSelector.OUT) {
        swapDispatcher({
          type: 'selected-out',
          selection: currency,
        })
      } else {
        console.error('State error on selector')
      }
      tokenSelectorDispatcher({ type: 'selected', selection: currency })
    }
  }

  const updateTokenQuantity = (t: ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(t.target.value))) {
      return
    }
    if (t.target.value.includes('e')) {
      return
    }

    // if no tokenIn selected, do nothing
    if (!swapTokenIn) {
      return
    }

    swapDispatcher({ type: 'value-typed', valueText: t.target.value })
  }

  /**
   * Values are checked before calling this function
   */
  async function swapTokenQuickEstimation(swap: SwapObject): Promise<Wei> {
    if (!web3Context.web3 || !account) {
      throw 'State badly defined' // should not arrive there, it is already checked before
    }

    const swapApi = new SwapApi(web3Context.web3, account, poolContractAddress)

    const estimatedOutWei = await swapApi.estimateTokenOutAmountCall(swap)
    swap.setExpectedOutAmount(estimatedOutWei)
    await wait(Math.random() * 1000)
    return estimatedOutWei
  }

  async function realSwap(): Promise<void> {
    const { web3, account, appChainID, proxy } = web3Context

    if (
      !swap ||
      !web3 ||
      !account ||
      !swapTokenInText ||
      !tokenInBalance ||
      !expectedOutAmountWei ||
      !swapTokenIn
    ) {
      console.error('state error: should have swap and web3')
      return
    }

    if (state !== SwapState.PREVIEW) {
      console.error('State should be on preview')
      return
    }
    const isMax = checkMax(
      swapTokenInText,
      tokenInBalance,
      maxSelected,
      swapTokenIn.decimals
    )

    const inAmountWei = isMax
      ? tokenInBalance || '0'
      : numericTextAsWei(swapTokenInText || '0', swapTokenIn.decimals)!

    swap.setInAmountWei(inAmountWei)

    const proxyApi = new ProxyApi(web3, account, appChainID, proxy)

    const onSwapPending = (hash: string, swap: SwapObject) => {
      swap.transactionHash = hash
      dispatcher({
        type: 'submit',
        tx: {
          uiRef: mounted,
          nonce: -1,
          status: 'pending',
          chainId: appChainID,
          account,
          type: 'swap',
          submitted: {
            hash,
            speedUp: false,
            start: new Date().getTime(),
          },
          data: swap.serialize(),
          swap: swap.serialize(),
        },
      })
    }

    const onSwapSuccess = (txHash: string, swap: SwapObject) => {
      setSuccessfulSwap(swap)
      dispatcher({
        type: 'complete',
        hash: swap.transactionHash,
        data: swap.serialize(),
      })
      swapDispatcher({ type: 'success', swap })

      // HACK: On polygon with metamask, there is an unprecised delay
      // It doesn't seem related to a confirmation number or fix delay, so just trying fibonacci timeouts
      setTimeout(() => {
        loadBalances()
      }, 7_000)
      setTimeout(() => {
        loadBalances()
      }, 20_000)
      setTimeout(() => loadBalances(), 40_000)
    }

    /**
     * txHash undefined if it fails before node creates it
     */
    const onSwapError = (txHash: string | undefined, errorMessage: string) => {
      setSuccessfulSwap(undefined)
      funnelDispatcher({ type: 'show-error', errorMessage })
      dispatcher({
        type: 'fail',
        hash: swap.transactionHash,
      })
      swapDispatcher({
        type: 'failed',
        data: {
          step: txHash === undefined ? 'not_accepted' : 'reverted',
          reason: errorMessage,
        },
        swap,
      })
    }

    try {
      funnelDispatcher({ type: 'submitting', swap })

      await proxyApi.swapIn(swap, onSwapPending, onSwapSuccess, onSwapError)
      // now transaction is in pending state
      resetSwapParametersState()
    } catch (error: any) {
      // happens when node was not able to accept the tx
      const errorMessage = extractErrorMessage(error)
      // TODO: could be called twice
      onSwapError(undefined, errorMessage)
    }
  }

  async function loadBalances() {
    if (!web3Context.web3 || !account) {
      balanceInDispatcher({ type: 'disconnect' })
      balanceOutDispatcher({ type: 'disconnect' })
      return
    }
    if (swapTokenIn) {
      balanceInDispatcher({ type: 'loading', token: swapTokenIn })

      try {
        const bal = await new TokenApi(
          web3Context.web3,
          account,
          swapTokenIn
        ).getERC20Balance()
        balanceInDispatcher({ type: 'loaded', value: bal })
      } catch (e) {
        // TODO: should display this error somewhere
        console.error(e)
        balanceInDispatcher({ type: 'loaded' })
      }
    }
    if (swapTokenOut) {
      balanceOutDispatcher({ type: 'loading', token: swapTokenOut })

      try {
        const bal = await new TokenApi(
          web3Context.web3,
          account,
          swapTokenOut
        ).getERC20Balance()
        balanceOutDispatcher({ type: 'loaded', value: bal })
      } catch (e) {
        console.error(e)
        balanceOutDispatcher({ type: 'loaded' })
      }
    }
  }

  function getVariationPercentage(): number | undefined {
    if (!tokenInValue || !tokenOutValue) {
      return undefined
    } else {
      return ((tokenOutValue - tokenInValue) / tokenInValue) * 100
    }
  }

  // TODO: this needs more refresh management
  function getCurrentAmounts() {
    const amounts = new Amounts()
    if (swapTokenIn && balanceIn.value) {
      amounts.set(swapTokenIn, balanceIn.value)
    }
    if (swapTokenOut && balanceOut.value) {
      amounts.set(swapTokenOut, balanceOut.value)
    }
    return amounts
  }

  const isSupported = web3Context.appChainID === getSupportedChain().appId
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <EmptyLine block />
      <SwapPanel>
        <div className={'justify-between'}>
          <SwapTitle className="">
            <span className="">Trade</span>
            <span className="m10"> </span>
            <span className="light">tokens</span>
          </SwapTitle>
          <Gear
            onClick={() => {
              setShowSettings(true)
              createTradeTabEvent().clicked_settings(slippage)
            }}
            className={'mr20 align-center'}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <SwapInputPanel>
            <div className={'justify-between'}>
              <SwapInput
                exceed={exceed}
                onChange={updateTokenQuantity}
                placeholder={numericalPlaceholder}
                value={swapTokenInText}
                data-cy={'in-value'}
              />
              <SwapInputCurrency
                onClick={() => {
                  tokenSelectorDispatcher({
                    type: 'select-in',
                    currentToken: swapTokenIn,
                  })
                }}
                currency={swapTokenIn}
              />
            </div>

            {swapTokenIn && showTokenInBalance && (
              <BalanceContainer>
                <ValueContainer>
                  {tokenInValue != undefined
                    ? `$ ${priceFormatter(tokenInValue)}`
                    : ''}{' '}
                </ValueContainer>

                <div className={'flex-row'}>
                  <SimpleBalance
                    exceed={exceed}
                    balance={tokenInBalance}
                    decimals={swapTokenIn.decimals}
                    loading={tokenInLoading}
                  />
                  <MaxButton
                    onMaxClick={() => {
                      tokenInBalance &&
                        swapTokenIn &&
                        swapDispatcher({
                          type: 'max',
                          maxValue: tokenInBalance,
                          valueText: weiToNumericText(
                            tokenInBalance,
                            swapTokenIn.decimals
                          ),
                        })
                    }}
                  />
                </div>
              </BalanceContainer>
            )}
          </SwapInputPanel>

          <ArrowDownAbsoluteContainer>
            <ArrowDown
              onSwitch={() => {
                // also switch balances
                let b = tokenInBalance
                balanceInDispatcher({ type: 'loaded', value: tokenOutBalance })
                balanceOutDispatcher({ type: 'loaded', value: b })
                swapDispatcher({ type: 'switch' })
              }}
            />
          </ArrowDownAbsoluteContainer>
        </div>

        <SwapInputPanel>
          <div className={'justify-between'}>
            <SwapInput
              value={displayedOutput(
                state,
                swapTokenInText,
                expectedOutAmountWei,
                swapTokenOut
              )}
              placeholder={numericalPlaceholder}
              readOnly
            />
            <CurrencyWrapper>
              <SwapInputCurrency
                onClick={() => {
                  tokenSelectorDispatcher({
                    type: 'select-out',
                    currentToken: swapTokenOut,
                  })
                }}
                currency={swapTokenOut}
              />
            </CurrencyWrapper>
          </div>
          {swapTokenOut && showTokenOutBalance && (
            <BalanceContainer>
              <ValueContainer>
                {tokenOutValue != undefined
                  ? `$ ${priceFormatter(tokenOutValue)}`
                  : ''}{' '}
                <VariationContainer value={getVariationPercentage()} />
              </ValueContainer>
              <SimpleBalance
                balance={tokenOutBalance}
                loading={tokenOutLoading}
                decimals={swapTokenOut.decimals}
              />
            </BalanceContainer>
          )}
        </SwapInputPanel>
        <div style={{ paddingRight: '2rem' }}>
          <NoFlickeringSwapQuotationLoader
            loading={currentSwap.estimating}
            inCurrency={swapTokenIn}
            outCurrency={swapTokenOut}
            inQuantity={swapTokenInText}
            outAmountWei={expectedOutAmountWei}
            tokenInValue={tokenInValue}
            tokenOutValue={tokenOutValue}
          />
        </div>

        {state !== PREVIEW && (
          <SwapButton
            handleOnClick={() => {
              funnelDispatcher({ type: 'preview', swap })
            }}
            // TODO: not pretty
            isReadyToSwap={
              expectedOutAmountWei !== undefined &&
              (state === SwapState.SWAPPABLE ||
                state === SwapState.ACCEPTANCE_FAILED)
            }
            message={
              isSupported
                ? exceed
                  ? `Not enough ${swapTokenIn?.symbol}`
                  : state.getMessage()
                : `Select ${getSupportedChain().chain} network`
            }
          />
        )}
      </SwapPanel>
      {tokenSelectorState.displayed && (
        <SwapCurrencySelectorPanel
          onExit={() => {
            tokenSelectorDispatcher({ type: 'cancel' })
          }}
          validateCurrency={validateCurrency}
          selectCurrency={selectCurrency}
          showCurrencySelector={tokenSelectorState.displayed}
          currencies={whitelist}
        />
      )}
      {state === SwapState.PREVIEW && swap && (
        <PreSwapPopup
          onExit={() => {
            funnelDispatcher({ type: 'swappable' })
          }}
          swap={swap as SwapObject & ValidSwap}
          state={state}
          onSwap={realSwap}
        />
      )}
      {successfulSwap && (
        <PostSwapPopup
          onExit={() => {
            loadBalances()
            setSuccessfulSwap(undefined)
          }}
          swap={successfulSwap as SwapObject & SuccessSwap}
          state={state}
        />
      )}
      {errorMessage && (
        <FailPostSwapPopup
          onExit={() => {
            if (state.isAfter(SwapState.SWAPPABLE)) {
              // The user was able to swap, then an error produced. The input conditions have
              // not change. Most probably 4001 - transaction refused.
              // When we will make some automatic refresh, it could be better to recheck if conditions from swappable to preview are still applicable.
              funnelDispatcher({ type: 'swappable' })
            } else {
              // we are catching an old transaction, and we are already working on a new transaction
              // So we just remove the error message popup
              funnelDispatcher({ type: 'clear-error' })
            }
          }}
          errorMessage={errorMessage}
        />
      )}
      {showSettings && (
        <Settings
          onExit={() => setShowSettings(false)}
          currentSlippage={slippage}
          onSlippageChange={(newSlippage: number) => {
            setSlippage(newSlippage)
            if (swap) {
              swap.slippage = newSlippage
            }
            createTradeTabEvent().changed_slippage_tolerance(
              slippage,
              newSlippage
            )
          }}
          context={'trade'}
        />
      )}
    </div>
  )
}

// TODO: move this function into swap reducer logic
export function isValidAmount(val: string | undefined, strictly = true) {
  if (!val) {
    return false
  }
  const float = parseFloat(val)
  return !isNaN(float) && (strictly ? float > 0 : float >= 0)
}

const CurrencyWrapper = styled.div`
  width: 200px;
  display: flex;
  justify-content: flex-end;
`
const ValueContainer = styled.div`
  justify-content: space-between;
  display: flex;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  font-size: ${SwapStyle.normalText};
  color: ${({ theme }) => theme.shadowText};
  font-family: Montserrat-Medium, Montserrat, serif;
`
const VariationContainer = styled.div<{ value?: number }>`
  justify-content: space-between;
  display: flex;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  font-size: ${SwapStyle.normalText};
  color: ${({ theme, value }) =>
    value && value < 3 ? theme.shadowText : swaapRed0};
  font-family: Montserrat-Medium, Montserrat, serif;
`

const BalanceContainer = styled.div`
  justify-content: space-between;
  display: flex;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
`

/**
 * There is a Max flag, but in case of front bug which often happen,
 * we must be sure we don't send max amount.
 * Also we can't just send what is in the input because of approximations.
 * So this function will ensure that what is written is very close to
 * the user balance.
 */
function checkMax(
  inputAmount: string,
  inBalance: Wei,
  isMax: boolean,
  decimals: number
) {
  if (!isMax) {
    return false
  }

  const input = parseFloat(inputAmount)
  const balance = fromWei(inBalance, decimals)
  if (balance === 0) {
    return false
  }
  const ratio = (Math.abs(input - balance) / balance) * 100
  return ratio < 0.001
}

function displayedOutput(
  state: ViewState,
  amountInWei: Wei | undefined,
  amountWei: Wei | undefined,
  swapTokenOut: Token | undefined
): string {
  const EMPTY = ''
  if (!amountInWei) {
    return EMPTY
  }
  if (!amountWei) {
    return EMPTY
  }

  if (!swapTokenOut) {
    return EMPTY
  }

  return priceFormatter(fromWei(amountWei, swapTokenOut.decimals), 8)
}

interface QuotationsProps {
  loading: boolean
  inCurrency?: Token
  outCurrency?: Token
  inQuantity?: string
  outAmountWei?: Wei
  tokenInValue?: number | undefined
  tokenOutValue?: number | undefined
}
const SwapQuotationLoader = (props: QuotationsProps) => {
  return <SwapQuotation {...props} />
}

// component should not update if it's loading
const NoFlickeringSwapQuotationLoader = React.memo(
  SwapQuotationLoader,
  (prev, next) => next.loading
)

function createSwapObject(
  config: ChainConfiguration,
  currentSwap: SwapReducerState,
  pool: Address,
  slippage: number,
  actionTreatmentNonce: number
): SwapObject {
  if (!currentSwap.tokenIn || !currentSwap.tokenOut) {
    throw 'invalid swap'
  }
  const newSwap = new SwapObject(
    config,
    pool,
    currentSwap.tokenIn,
    currentSwap.tokenOut,
    actionTreatmentNonce
  )
  newSwap.slippage = slippage
  newSwap.setInAmountWei(
    numericTextAsWei(currentSwap.tokenInText, currentSwap.tokenIn.decimals)!
  )
  return newSwap
}
